import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {IWorkbookSnapshot, Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';

export interface ISpreadsheetsStateSnapshot
{
    rootWorkbookId:WorkbookId;
    workbooks:IWorkbooksSnapshot;
}

export interface IWorkbooksSnapshot
{
    [workbookId:string]:IWorkbookSnapshot;
}

export class SpreadsheetsState
{
    @observable
    public rootWorkbook:Workbook = null;

    @observable
    public workbooks:Map<WorkbookId, Workbook> = new Map();

    private constructor()
    {
    }

    public static create():SpreadsheetsState
    {
        const spreadsheetsState:SpreadsheetsState = new SpreadsheetsState();

        const workbook:Workbook = Workbook.create();

        spreadsheetsState.rootWorkbook = workbook;
        spreadsheetsState.workbooks.set(workbook.id, workbook);

        return spreadsheetsState;
    }

    @computed
    public get sheetToWorkbookMap():Map<SheetId, Workbook>
    {
        const sheetToWorkbookMap:Map<SheetId, Workbook> = new Map();

        this.workbooks.forEach((workbook:Workbook) => (
            workbook.runtime.sheets.forEach((sheet:Sheet) => sheetToWorkbookMap.set(sheet.id, workbook))
        ));

        return sheetToWorkbookMap;
    }

    public static toJson:ITransformer<SpreadsheetsState, ISpreadsheetsStateSnapshot> = createTransformer((spreadsheetsState:SpreadsheetsState) =>
    {
        return {
            rootWorkbookId: spreadsheetsState.rootWorkbook.id,
            workbooks: Array.from(spreadsheetsState.workbooks.entries()).reduce((result, [workbookId, workbook]) =>
            {
                result[workbookId] = Workbook.toJson(workbook);
                return result;
            }, {})
        };
    });

    public static fromJson(json:ISpreadsheetsStateSnapshot,
                           currentState?:SpreadsheetsState,
                           currentJson?:ISpreadsheetsStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):SpreadsheetsState
    {
        if( !currentState )
            return SpreadsheetsState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = SpreadsheetsState.toJson(currentState);

        const spreadsheetsState:SpreadsheetsState = new SpreadsheetsState();

        if( json.workbooks === currentJson.workbooks )
            spreadsheetsState.workbooks = currentState.workbooks;
        else
        {
            const workbooks:Map<WorkbookId, Workbook> = new Map();

            Object.entries(json.workbooks).forEach(([workbookId, workbook]) =>
            {
                const workbookCurrentState:Workbook = currentState.workbooks.get(workbookId);
                const workbookCurrentJson:IWorkbookSnapshot = currentJson.workbooks[workbookId];

                if( json.workbooks[workbookId] === currentJson.workbooks[workbookId] )
                    workbooks.set(workbookId, workbookCurrentState);
                else
                    workbooks.set(workbookId, Workbook.fromJson(workbook, workbookCurrentState, workbookCurrentJson, ctx));
            });

            spreadsheetsState.workbooks = workbooks;
        }

        spreadsheetsState.rootWorkbook = spreadsheetsState.workbooks.get(json.rootWorkbookId);

        return spreadsheetsState;
    }

    public static fromJsonDeep(json:ISpreadsheetsStateSnapshot):SpreadsheetsState
    {
        const spreadsheetsState:SpreadsheetsState = new SpreadsheetsState();

        const workbooks:Map<WorkbookId, Workbook> = new Map();

        Object.entries(json.workbooks)
            .forEach(([workbookId, workbook]) => workbooks.set(workbookId, Workbook.fromJsonDeep(workbook)));

        spreadsheetsState.workbooks = workbooks;
        spreadsheetsState.rootWorkbook = workbooks.get(json.rootWorkbookId);

        return spreadsheetsState;
    }
}
